import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search,
  Refresh,
  Visibility,
  Receipt,
  AttachMoney,
  CheckCircle,
  Pending,
  Description,
} from "@mui/icons-material";
import axios from "axios";
import logger from "../../utils/logger";

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    cancelled: 0,
    total_amount: 0,
    paid_amount: 0,
  });

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    const token = localStorage.getItem("superAdminToken");
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5000/api"
        }/super-admin/invoices`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const invoicesData = response.data.data;
      setInvoices(invoicesData);

      // حساب الإحصائيات
      const stats = {
        total: invoicesData.length,
        paid: invoicesData.filter((i) => i.status === "paid").length,
        pending: invoicesData.filter((i) => i.status === "pending").length,
        cancelled: invoicesData.filter((i) => i.status === "cancelled").length,
        total_amount: invoicesData.reduce(
          (sum, i) => sum + parseFloat(i.amount || 0),
          0
        ),
        paid_amount: invoicesData
          .filter((i) => i.status === "paid")
          .reduce((sum, i) => sum + parseFloat(i.amount || 0), 0),
      };
      setStats(stats);
    } catch (error) {
      logger.error("Error fetching invoices:", error);
      showSnackbar("فشل تحميل الفواتير", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    const token = localStorage.getItem("superAdminToken");
    try {
      await axios.put(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5000/api"
        }/super-admin/invoices/${invoiceId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("تم تحديث حالة الفاتورة بنجاح", "success");
      fetchInvoices();
      setDetailsDialog(false);
    } catch (error) {
      logger.error("Error updating invoice status:", error);
      showSnackbar("فشل تحديث حالة الفاتورة", "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "cancelled":
        return "error";
      case "overdue":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "paid":
        return "مدفوعة";
      case "pending":
        return "قيد الانتظار";
      case "cancelled":
        return "ملغاة";
      case "overdue":
        return "متأخرة";
      default:
        return status;
    }
  };

  const filteredInvoices = invoices.filter(
    (invoice) =>
      invoice.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ar-IQ", {
      style: "currency",
      currency: "IQD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          إدارة الفواتير
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchInvoices}
        >
          تحديث
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    إجمالي الفواتير
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </Box>
                <Receipt
                  sx={{ fontSize: 50, color: "primary.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    المدفوعة
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {stats.paid}
                  </Typography>
                </Box>
                <CheckCircle
                  sx={{ fontSize: 50, color: "success.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    قيد الانتظار
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="warning.main"
                  >
                    {stats.pending}
                  </Typography>
                </Box>
                <Pending
                  sx={{ fontSize: 50, color: "warning.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    إجمالي المبلغ
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {formatCurrency(stats.paid_amount)}
                  </Typography>
                </Box>
                <AttachMoney
                  sx={{ fontSize: 50, color: "primary.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="البحث في الفواتير..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Table */}
      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>رقم الفاتورة</strong>
                </TableCell>
                <TableCell>
                  <strong>المستأجر</strong>
                </TableCell>
                <TableCell>
                  <strong>المبلغ</strong>
                </TableCell>
                <TableCell>
                  <strong>تاريخ الإصدار</strong>
                </TableCell>
                <TableCell>
                  <strong>تاريخ الاستحقاق</strong>
                </TableCell>
                <TableCell>
                  <strong>الحالة</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>الإجراءات</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={3}>
                      لا توجد فواتير
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>
                      <Chip
                        label={invoice.invoice_number || `INV-${invoice.id}`}
                        size="small"
                        variant="outlined"
                        icon={<Description />}
                      />
                    </TableCell>
                    <TableCell>{invoice.tenant_name}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="primary">
                        {formatCurrency(invoice.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(invoice.issue_date).toLocaleDateString("ar-IQ")}
                    </TableCell>
                    <TableCell>
                      {new Date(invoice.due_date).toLocaleDateString("ar-IQ")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(invoice.status)}
                        color={getStatusColor(invoice.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="عرض التفاصيل">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setDetailsDialog(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تفاصيل الفاتورة</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    رقم الفاتورة
                  </Typography>
                  <Typography variant="h6">
                    {selectedInvoice.invoice_number ||
                      `INV-${selectedInvoice.id}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    المستأجر
                  </Typography>
                  <Typography>{selectedInvoice.tenant_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    المبلغ
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(selectedInvoice.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    تاريخ الإصدار
                  </Typography>
                  <Typography>
                    {new Date(selectedInvoice.issue_date).toLocaleDateString(
                      "ar-IQ"
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    تاريخ الاستحقاق
                  </Typography>
                  <Typography>
                    {new Date(selectedInvoice.due_date).toLocaleDateString(
                      "ar-IQ"
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    الحالة الحالية
                  </Typography>
                  <Chip
                    label={getStatusText(selectedInvoice.status)}
                    color={getStatusColor(selectedInvoice.status)}
                  />
                </Grid>
                {selectedInvoice.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      الوصف
                    </Typography>
                    <Typography>{selectedInvoice.description}</Typography>
                  </Grid>
                )}
              </Grid>

              {selectedInvoice.status === "pending" && (
                <Box mt={3}>
                  <Alert severity="info">
                    يمكنك تحديث حالة الفاتورة من هنا
                  </Alert>
                  <Box display="flex" gap={2} mt={2}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() =>
                        handleUpdateStatus(selectedInvoice.id, "paid")
                      }
                    >
                      تحديد كمدفوعة
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() =>
                        handleUpdateStatus(selectedInvoice.id, "cancelled")
                      }
                    >
                      إلغاء الفاتورة
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Invoices;
